import Phaser from 'phaser';

/**
 * Función opcional para nombrar cada frame. Recibe (row, col, index row-major) y
 * devuelve el nombre/número con el que se registra el frame. Por defecto se usa
 * el índice numérico `index` (compatible con `generateFrameNumbers`).
 */
export type FrameNameFn = (
  row: number,
  col: number,
  index: number
) => string | number;

/**
 * Corta una imagen en un grid de columns×rows y registra cada celda como un
 * frame. Por defecto los frames se nombran `0..N-1` (orden row-major), igual que
 * `addSpriteSheet`; pasá `nameFor` para usar nombres custom (ej. `r{row}c{col}`).
 *
 * A diferencia de `addSpriteSheet` (que usa un `frameWidth`/`frameHeight` entero
 * uniforme y trunca con floor), este helper redondea los **bordes acumulados** de
 * cada columna/fila. Los frames adyacentes comparten exactamente el mismo límite,
 * así que NO hay drift acumulado ni sangrado entre frames cuando el PNG no mide un
 * múltiplo exacto de (columns, rows). Cada frame puede variar ±1px de ancho/alto,
 * lo cual es imperceptible y mantiene el contenido alineado.
 *
 * Con el naming por defecto, `generateFrameNumbers(key, {start, end})` y los
 * helpers `getXxxIdleFrame` siguen funcionando sin cambios.
 *
 * @returns la textura creada, o `null` si no se pudo crear.
 */
export function addGridSpriteSheet(
  scene: Phaser.Scene,
  key: string,
  source: HTMLImageElement | HTMLCanvasElement,
  columns: number,
  rows: number,
  nameFor?: FrameNameFn
): Phaser.Textures.Texture | null {
  const imgW = source.width;
  const imgH = source.height;

  if (scene.textures.exists(key)) {
    scene.textures.remove(key);
  }

  // addImage acepta canvas en runtime, pero los typings sólo declaran Image.
  const tex = scene.textures.addImage(key, source as HTMLImageElement);
  if (!tex) return null;

  // Bordes redondeados: colX[c] = round(c * W / columns). Adyacentes comparten
  // límite, por lo que no hay gaps ni overlaps y el error nunca se acumula.
  const colX = Array.from({ length: columns + 1 }, (_, c) =>
    Math.round((c * imgW) / columns)
  );
  const rowY = Array.from({ length: rows + 1 }, (_, r) =>
    Math.round((r * imgH) / rows)
  );

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < columns; c++) {
      const index = r * columns + c;
      const name = nameFor ? nameFor(r, c, index) : index;
      tex.add(
        name,
        0,
        colX[c],
        rowY[r],
        colX[c + 1] - colX[c],
        rowY[r + 1] - rowY[r]
      );
    }
  }

  return tex;
}

/**
 * Dimensiones nominales de frame para un grid columns×rows sobre una imagen de
 * `imgW`×`imgH`. Devuelve el tamaño redondeado (no floored), pensado para origen
 * y centrado de sprites. El frame real de cada celda puede variar ±1px (ver
 * {@link addGridSpriteSheet}), pero el anclaje usa este valor nominal.
 */
export function getNominalFrameSize(
  imgW: number,
  imgH: number,
  columns: number,
  rows: number
): { width: number; height: number } {
  return {
    width: Math.round(imgW / columns),
    height: Math.round(imgH / rows),
  };
}
